import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionableNotification,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { debounce } from 'lodash-es';
import {
  isPercentOfPackageShownAtom,
  rollupBinAtom,
  rollupCashflowPeriodAtom,
  rollupDimensionsAtom,
  rollupSearchAtom,
  rollupSearchQueryAtom,
  rollupSortAtom,
} from './atoms';
import { useRollupCardContext, useValueRollup } from './hooks';
import { ApiEnvelopeError } from 'lib/evaluation-case/api-envelope';
import { rollupHasGrouping } from './utils';
import { TemporalBinSummary } from './temporal-bin-summary';
import { ValueRollupTable } from './value-rollup-table';
import type {
  RollupColumn,
  RollupDimensionOption,
  RollupOptions,
  RollupRow,
  RollupTotals,
} from './types';
import styles from './value-rollup-table-card.module.scss';

/** Page size the rollup opens on (one of `MASTER_DATA_PAGE_SIZES`; mirrors master_data). */
const INITIAL_PAGE_SIZE = 100;

/** How long a search keystroke waits before minting a new query key (mirrors cashflows). */
const SEARCH_DEBOUNCE_MS = 300;

// Stable empty fallbacks so a "no options / no data yet" render doesn't mint fresh array
// identities that would churn the render-time reset + the table's memos.
const EMPTY_DIMENSIONS: string[] = [];
const EMPTY_OPTIONS: RollupDimensionOption[] = [];
const EMPTY_COLUMNS: RollupColumn[] = [];
const EMPTY_ROWS: RollupRow[] = [];
const EMPTY_TOTALS: RollupTotals = {};
const EMPTY_BINS: RollupOptions['bins'] = { numeric: [], temporal: [] };

export interface ValueRollupTableCardProps {
  /** The shared options catalog (fetched once by the section shell), or undefined until it resolves. */
  options: RollupOptions | undefined;
  /** Whether the shared catalog is still loading (folds into the table's loading umbrella). */
  optionsLoading: boolean;
}

/**
 * Connected Value Rollup TABLE card — subscribes to the TABLE view-state atoms only
 * (dimensions / sort / search / period / % of Package), owns the offset page state +
 * the page-reset latch, runs `useValueRollup`, and drives the presentational
 * `ValueRollupTable`. Split out of the section (from the chart card) so a table-only
 * change re-renders only this card. Reads the shared filters / spatial scope, so a
 * left-panel change re-renders both cards (and refetches both). Its data error shows as
 * a Retry notification above the card; the shared options error lives on the shell.
 *
 * @param {ValueRollupTableCardProps} props - The shared options catalog + its loading flag.
 * @return {JSX.Element} The Value Rollup table card.
 */
export function ValueRollupTableCard({
  options,
  optionsLoading,
}: ValueRollupTableCardProps) {
  const {
    packageId,
    calculationId,
    status,
    isRunning,
    filters,
    mapScope,
    labelForName,
  } = useRollupCardContext();

  // View-state. `sort` is read+write; `search` is the IMMEDIATE text the input shows
  // (controlled), while `searchQuery` is the DEBOUNCED value the query keys on.
  const [dimensionsAtomValue, setDimensions] = useAtom(rollupDimensionsAtom);
  const [sort, setSort] = useAtom(rollupSortAtom);
  const [search, setSearch] = useAtom(rollupSearchAtom);
  const [searchQuery, setSearchQuery] = useAtom(rollupSearchQueryAtom);
  const [period, setPeriod] = useAtom(rollupCashflowPeriodAtom);
  const [showPercent, setShowPercent] = useAtom(isPercentOfPackageShownAtom);
  const [bin, setBin] = useAtom(rollupBinAtom);

  // Effective dims = the persisted atom, else the config default — no seeding effect;
  // FIRST-EVER open chains after options resolves (the query is gated on non-empty dims).
  const effectiveDimensions = useMemo(
    () =>
      dimensionsAtomValue ?? options?.defaults.dimensions ?? EMPTY_DIMENSIONS,
    [dimensionsAtomValue, options?.defaults.dimensions]
  );

  // Single page owner (the `useMasterDataView` pattern): page / pageSize are local state,
  // handed to the table as controlled pagination.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);

  // Reset to page 1 whenever any query input changes — the React-documented "adjust state
  // during render" pattern (prev tracked in STATE, not a ref, so StrictMode's
  // double-invoked render stays consistent). `effectivePage` is computed inline and fed to
  // BOTH the fetch and the controlled pager, so the key is page 1 immediately (no
  // transient request for the old page). Reference compares suffice: the atoms/derived
  // inputs keep stable identities between real changes.
  const [prevInputs, setPrevInputs] = useState({
    effectiveDimensions,
    sort,
    searchQuery,
    period,
    bin,
    filters,
    mapScope,
  });
  let effectivePage = page;
  if (
    prevInputs.effectiveDimensions !== effectiveDimensions ||
    prevInputs.sort !== sort ||
    prevInputs.searchQuery !== searchQuery ||
    prevInputs.period !== period ||
    prevInputs.bin !== bin ||
    prevInputs.filters !== filters ||
    prevInputs.mapScope !== mapScope
  ) {
    setPrevInputs({
      effectiveDimensions,
      sort,
      searchQuery,
      period,
      bin,
      filters,
      mapScope,
    });
    effectivePage = 1;
    setPage(1);
  }

  const {
    data: rollupPage,
    isFetching,
    error,
    refetch,
  } = useValueRollup({
    calcId: calculationId,
    packageId,
    status,
    dimensions: effectiveDimensions,
    page: effectivePage,
    pageSize,
    sort,
    search: searchQuery,
    cashflowPeriod: period,
    filters,
    mapScope,
    bin,
  });

  const onPaginationChange = useCallback(
    (newPage: number, newPageSize: number) => {
      setPage(newPage);
      setPageSize(newPageSize);
    },
    []
  );

  // Memoized so the table's row-model / column memos aren't busted by a fresh object each
  // render; `keepPreviousData` keeps the prior page visible through a transition. With NO
  // dimensions OR no calc (workspace / draft) the query is disabled but `keepPreviousData`
  // still holds the last grouping's rows — blank the data explicitly so the table shows
  // its empty state instead of stale rows.
  const tableData = useMemo(() => {
    // Blank when there's no calc OR nothing to group by. A numeric bin is itself a
    // grouping (a bin-only rollup), so it keeps the fetched data even with no dimensions.
    const hasGrouping = rollupHasGrouping(effectiveDimensions, bin);
    if (calculationId === null || !hasGrouping) {
      return {
        columns: EMPTY_COLUMNS,
        rows: EMPTY_ROWS,
        totals: EMPTY_TOTALS,
        periodSummary: null,
        totalCount: 0,
      };
    }
    return {
      columns: rollupPage?.columns ?? EMPTY_COLUMNS,
      rows: rollupPage?.rows ?? EMPTY_ROWS,
      totals: rollupPage?.totals ?? EMPTY_TOTALS,
      periodSummary: rollupPage?.cashflowPeriodSummary ?? null,
      totalCount: rollupPage?.totalCount ?? 0,
    };
  }, [rollupPage, effectiveDimensions, calculationId, bin]);
  const pagination = useMemo(
    () => ({ page: effectivePage, pageSize, onChange: onPaginationChange }),
    [effectivePage, pageSize, onPaginationChange]
  );

  // Debounce the query WRITE (not the input): each keystroke updates `search` (immediate,
  // controlled — the input stays responsive) but only mints a new query key after the
  // pause. `setSearchQuery` is a stable atom setter, so the debounced wrapper is created
  // once; cancel a pending trailing write on unmount.
  const debouncedSetSearchQuery = useMemo(
    () => debounce(setSearchQuery, SEARCH_DEBOUNCE_MS),
    [setSearchQuery]
  );
  useEffect(
    () => () => debouncedSetSearchQuery.cancel(),
    [debouncedSetSearchQuery]
  );

  // The config dimension keys — used to tell a removed DIMENSION (whose sort must clear,
  // FR#7) from a METRIC (whose sort persists — metrics are always returned).
  const dimensionOptionKeys = useMemo(
    () => options?.dimensions.map((option) => option.key) ?? EMPTY_DIMENSIONS,
    [options?.dimensions]
  );

  // Group-by (live): the control writes the persisted atom; the effective selection
  // (atom ?? defaults) drives both the picker value and the query, and a change flows
  // through the render-time reset above → page 1 + refetch. FR#7: if the sorted column is
  // a dimension being removed, clear the sort (a no-op functional update otherwise).
  const handleGroupByChange = useCallback(
    (keys: string[]) => {
      setDimensions(keys);
      setSort((current) => {
        const isRemovedDimension =
          current !== null &&
          dimensionOptionKeys.includes(current.field) &&
          !keys.includes(current.field);
        return isRemovedDimension ? null : current;
      });
    },
    [setDimensions, setSort, dimensionOptionKeys]
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearch(query);
      debouncedSetSearchQuery(query);
    },
    [setSearch, debouncedSetSearchQuery]
  );

  const groupBy = useMemo(
    () => ({
      options: options?.dimensions ?? EMPTY_OPTIONS,
      selected: effectiveDimensions,
      onChange: handleGroupByChange,
    }),
    [options?.dimensions, effectiveDimensions, handleGroupByChange]
  );
  const sortProp = useMemo(
    () => ({ value: sort, onChange: setSort }),
    [sort, setSort]
  );
  const searchProp = useMemo(
    () => ({ value: search, onChange: handleSearchChange }),
    [search, handleSearchChange]
  );

  // % of Package — a pure show/hide of the per-row badges (no refetch; the fractions ride
  // every row). The period change goes through the render-time reset → page 1.
  const percentOfPackage = useMemo(
    () => ({ enabled: showPercent, onToggle: setShowPercent }),
    [showPercent, setShowPercent]
  );
  const periodProp = useMemo(
    () => ({
      value: period,
      onApply: setPeriod,
      onRemove: () => setPeriod(null),
    }),
    [period, setPeriod]
  );

  // Bin by (single value): the catalog presets + the active bin + its setter. Feeds the
  // table's Bin by menu + chip; a change flows through the render-time reset → page 1.
  const binProp = useMemo(
    () => ({
      options: options?.bins ?? EMPTY_BINS,
      value: bin,
      onChange: setBin,
    }),
    [options?.bins, bin, setBin]
  );

  // The active temporal preset's label — the summary table heading.
  const temporalBinLabel = useMemo(() => {
    if (bin?.category !== 'temporal') {
      return undefined;
    }
    return options?.bins.temporal.find((preset) => preset.key === bin.presetKey)
      ?.label;
  }, [bin, options?.bins]);

  // Loading umbrella = the shared catalog OR this card's own fetch OR the calc-lifecycle
  // wait (detail resolving / BE processing).
  const isLoading = optionsLoading || isFetching || isRunning;

  // This card surfaces only its OWN data error (the shared options error lives on the
  // shell). Suppressed while its fetch / the calc run is in flight — the sibling rule.
  const showError = Boolean(error) && !isLoading;

  // INS-12433 — a permanent failure (BE `retryable: false`, e.g. a calculation whose
  // slot-allocation snapshot was never written) can never succeed on retry, so the banner
  // states it without a button rather than inviting the user to hammer an endpoint that has
  // already said no. Anything untyped stays retryable.
  const canRetry = !(error instanceof ApiEnvelopeError && !error.retryable);

  // Show the temporal summary only while a temporal bin is the ACTIVE selection — gate on
  // the atom (not just the response) so `keepPreviousData` can't leave a stale summary
  // showing after switching to a numeric bin / None.
  const temporalSummary =
    bin?.category === 'temporal' &&
    rollupPage?.temporalBinColumns &&
    rollupPage.temporalBins
      ? {
          columns: rollupPage.temporalBinColumns,
          rows: rollupPage.temporalBins,
        }
      : null;

  return (
    <>
      {showError && error && canRetry && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the rollup"
          subtitle={error.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}

      {/* Permanent failure: same placement and kind, no Retry. `role="alert"` because
          InlineNotification defaults to the quieter "status", and the branch the user can do
          nothing about must not be announced more softly than the one they can act on. */}
      {showError && error && !canRetry && (
        <InlineNotification
          className={styles.banner}
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title="Couldn't load the rollup"
          subtitle={error.message}
        />
      )}

      <div className={styles.card}>
        <div className={styles.headingRow}>
          <h3 className={styles.cardTitle}>Value Rollup</h3>
          {isLoading && (
            <InlineLoading
              className={styles.headingSpinner}
              description=""
              aria-hidden
            />
          )}
        </div>
        {temporalSummary && (
          <TemporalBinSummary
            columns={temporalSummary.columns}
            rows={temporalSummary.rows}
            title={temporalBinLabel}
            cashflowPeriod={period}
          />
        )}
        <ValueRollupTable
          data={tableData}
          groupBy={groupBy}
          sort={sortProp}
          search={searchProp}
          percentOfPackage={percentOfPackage}
          period={periodProp}
          bin={binProp}
          isLoading={isLoading}
          pagination={pagination}
          labelForName={labelForName}
        />
      </div>
    </>
  );
}
